import Phaser from "phaser";
import { Cat } from "../sprites/CatSprite";
import { CoinBehaviour } from "../behaviours/CoinBehaviour";
import { CategoryData } from "../../config/types";

/* Helper for verbose transfer debug */
const dbg = (...args: any[]) => console.log("[TransferSystem]", ...args);

export class TransferSystem {
  private scene: Phaser.Scene;
  private cats: Cat[];
  private categories: CategoryData[];
  private onCategoriesChanged: (
    cats: Cat[],
    categories: CategoryData[],
  ) => void;
  private field: "spent" | "budgeted";

  constructor(
    scene: Phaser.Scene,
    cats: Cat[],
    categories: CategoryData[],
    onCategoriesChanged: (cats: Cat[], categories: CategoryData[]) => void,
    field: "spent" | "budgeted" = "spent",
  ) {
    this.scene = scene;
    this.cats = cats;
    this.categories = categories;
    this.onCategoriesChanged = onCategoriesChanged;
    this.field = field;
    dbg("initialized", {
      field,
      cats: cats.length,
      categories: categories.length,
    });
  }

  /**
   * Perform a kibble transfer and animate coins.
   */
  transfer(
    fromIdx: number,
    toIdx: number,
    amount: number,
    start: Phaser.Math.Vector2,
    end: Phaser.Math.Vector2,
  ) {
    dbg("transfer start", { fromIdx, toIdx, amount });
    const BUNDLE = Math.min(5, Math.floor(amount / 2));
    const STAGGER = 120;
    for (let i = 0; i < BUNDLE; i++) {
      new CoinBehaviour(this.scene, start, end, i * STAGGER);
    }
    this.applyTransfer(fromIdx, toIdx, amount);
  }

  private applyTransfer(fromIdx: number, toIdx: number, amount: number) {
    if (fromIdx >= this.categories.length || toIdx >= this.categories.length)
      return;

    dbg("applyTransfer before", {
      donor: { ...this.categories[fromIdx] },
      recipient: { ...this.categories[toIdx] },
    });

    if (this.field === "spent") {
      this.categories[fromIdx].spent = Math.max(
        0,
        this.categories[fromIdx].spent - amount,
      );
      this.categories[toIdx].spent += amount;
    } else {
      this.categories[fromIdx].budgeted = Math.max(
        0,
        this.categories[fromIdx].budgeted - amount,
      );
      this.categories[toIdx].budgeted += amount;
    }

    // Update heart visibility
    [fromIdx, toIdx].forEach((idx) => {
      const cat = this.cats[idx];
      const catData = this.categories[idx];
      cat.refreshFinancialStatus(catData.budgeted, catData.spent);
    });

    this.onCategoriesChanged(this.cats, this.categories);

    dbg("applyTransfer after", {
      donor: { ...this.categories[fromIdx] },
      recipient: { ...this.categories[toIdx] },
    });
  }
}
